import axios from "axios";

// Shopify Admin GraphQL does not return CORS headers for arbitrary origins,
// so direct browser calls will fail with "blocked by CORS policy" unless
// proxied. If REACT_APP_SHOPIFY_PROXY is set, requests go through that
// origin (recommended for production demos). Otherwise we hit Shopify
// directly — works only in environments with relaxed CORS (custom apps
// with allowed origins, or local dev with a CORS-relaxing extension).

const SHOP        = process.env.REACT_APP_SHOPIFY_SHOP        ?? "";
const ADMIN_TOKEN = process.env.REACT_APP_SHOPIFY_ADMIN_TOKEN ?? "";
const PROXY       = process.env.REACT_APP_SHOPIFY_PROXY       ?? "";
const API_VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION ?? "2024-10";

export const shopifyConfigured = Boolean(SHOP && ADMIN_TOKEN);
export const shopifyShopName   = SHOP;

function graphqlUrl(): string {
  if (PROXY) return `${PROXY.replace(/\/$/, "")}/admin/api/${API_VERSION}/graphql.json`;
  const host = SHOP.includes(".") ? SHOP : `${SHOP}.myshopify.com`;
  return `https://${host}/admin/api/${API_VERSION}/graphql.json`;
}

const headers = () => ({
  "X-Shopify-Access-Token": ADMIN_TOKEN,
  "Content-Type":           "application/json",
  "Accept":                 "application/json",
});

export class ShopifyError extends Error {
  constructor(public kind: "not_configured" | "cors" | "auth" | "not_found" | "network" | "graphql" | "unknown", message: string) {
    super(message);
    this.name = "ShopifyError";
  }
}

function classifyError(err: unknown): ShopifyError {
  if (axios.isAxiosError(err)) {
    if (!err.response) {
      const msg = err.message ?? "";
      if (/network|cors|preflight/i.test(msg)) return new ShopifyError("cors", "CORS-blocked — requires a server-side proxy.");
      return new ShopifyError("network", msg || "Network error.");
    }
    if (err.response.status === 401 || err.response.status === 403) {
      return new ShopifyError("auth", "Invalid Shopify token or insufficient scopes.");
    }
    if (err.response.status === 404) return new ShopifyError("not_found", "No matching record.");
    return new ShopifyError("unknown", `Shopify API error ${err.response.status}.`);
  }
  return new ShopifyError("unknown", err instanceof Error ? err.message : "Unknown error.");
}

function ensureConfigured() {
  if (!shopifyConfigured) {
    throw new ShopifyError("not_configured", "Set REACT_APP_SHOPIFY_SHOP and REACT_APP_SHOPIFY_ADMIN_TOKEN.");
  }
}

// ─── GraphQL transport ───────────────────────────────────────────────────────

interface GraphQLResponse<T> {
  data?:    T;
  errors?:  { message: string; extensions?: Record<string, unknown> }[];
}

async function gql<T>(query: string, variables: Record<string, unknown> = {}): Promise<T> {
  ensureConfigured();
  try {
    const { data } = await axios.post<GraphQLResponse<T>>(
      graphqlUrl(),
      { query, variables },
      { headers: headers() },
    );
    if (data.errors && data.errors.length > 0) {
      const first = data.errors[0];
      const accessDenied = data.errors.some((e) => /access denied|unauthorized|scope/i.test(e.message));
      throw new ShopifyError(accessDenied ? "auth" : "graphql", first.message);
    }
    if (!data.data) {
      throw new ShopifyError("graphql", "Empty GraphQL response.");
    }
    return data.data;
  } catch (err) {
    if (err instanceof ShopifyError) throw err;
    throw classifyError(err);
  }
}

// Strip Shopify global ID prefix → numeric id.  "gid://shopify/Customer/12345" → 12345
function parseGid(gid: string): number {
  if (!gid) return 0;
  const tail = gid.slice(gid.lastIndexOf("/") + 1);
  const n = parseInt(tail, 10);
  return Number.isFinite(n) ? n : 0;
}

// ─── Domain types (unchanged — consumers don't need to know about GraphQL) ──

export interface ShopifyOrderSummary {
  id: number;
  name: string;
  orderNumber: number;
  totalPrice: string;
  currency: string;
  financialStatus: string | null;
  fulfillmentStatus: string | null;
  createdAt: string;
  customerName: string;
  email: string | null;
  lineItems: { title: string; quantity: number; price: string; imageUrl?: string }[];
  trackingNumber: string | null;
  trackingCompany: string | null;
  trackingUrl: string | null;
  cancellable: boolean;
}

export interface ShopifyProductSummary {
  id: number;
  title: string;
  vendor: string;
  productType: string;
  price: string;
  compareAtPrice: string | null;
  imageUrl: string | null;
  available: boolean;
  inventoryQuantity: number;
}

export interface ShopifyLocationSummary {
  id: number;
  name: string;
  city: string | null;
  country: string | null;
  available: number;
}

export interface ShopifyCustomerSummary {
  id:           number;
  firstName:    string;
  lastName:     string;
  displayName:  string;
  email:        string | null;
  phone:        string | null;
  ordersCount:  number;
  totalSpent:   string;
  currency:     string;
  createdAt:    string;
  city:         string | null;
  country:      string | null;
  tags:         string[];
}

// ─── Queries ─────────────────────────────────────────────────────────────────

const ORDER_FIELDS = `
  id
  name
  legacyResourceId
  displayFinancialStatus
  displayFulfillmentStatus
  totalPriceSet { shopMoney { amount currencyCode } }
  createdAt
  email
  cancelledAt
  customer { firstName lastName }
  lineItems(first: 3) {
    edges {
      node {
        title
        quantity
        originalUnitPriceSet { shopMoney { amount } }
        image { url }
      }
    }
  }
  fulfillments(first: 1) {
    trackingInfo { number company url }
  }
`;

const PRODUCT_FIELDS = `
  id
  legacyResourceId
  title
  vendor
  productType
  status
  featuredImage { url }
  variants(first: 1) {
    edges {
      node {
        price
        compareAtPrice
        inventoryQuantity
        inventoryItem { id }
      }
    }
  }
`;

// ─── Endpoints ───────────────────────────────────────────────────────────────

export interface CustomerScope {
  customerId?: number;
  email?:      string;
}

function buildCustomerFilter(scope?: CustomerScope): string {
  if (!scope) return "";
  if (scope.customerId) return `customer_id:${scope.customerId}`;
  if (scope.email)      return `email:${scope.email}`;
  return "";
}

export async function fetchMostRecentOrder(scope?: CustomerScope): Promise<ShopifyOrderSummary | null> {
  const filter = buildCustomerFilter(scope);
  const query  = `query MostRecentOrder($q: String) {
    orders(first: 1, sortKey: CREATED_AT, reverse: true, query: $q) {
      edges { node { ${ORDER_FIELDS} } }
    }
  }`;
  const data = await gql<{ orders: { edges: { node: any }[] } }>(query, { q: filter || null });
  const node = data.orders.edges[0]?.node;
  return node ? normalizeOrder(node) : null;
}

export async function fetchCustomerOrders(scope?: CustomerScope, limit = 10): Promise<ShopifyOrderSummary[]> {
  // All orders for a customer, newest first — used by the customer-info Order
  // History section to list each order's line items (name + price).
  const filter = buildCustomerFilter(scope);
  const query  = `query CustomerOrders($q: String, $first: Int!) {
    orders(first: $first, sortKey: CREATED_AT, reverse: true, query: $q) {
      edges { node { ${ORDER_FIELDS} } }
    }
  }`;
  const data = await gql<{ orders: { edges: { node: any }[] } }>(query, { q: filter || null, first: limit });
  return data.orders.edges.map((e) => normalizeOrder(e.node));
}

export async function fetchOpenOrder(scope?: CustomerScope): Promise<ShopifyOrderSummary | null> {
  // "Open" = not cancelled and not fully delivered. This matches what a user
  // would call an in-flight order (paid-pending, in transit, partially
  // shipped, etc) rather than the stricter REST `status=open&financial_status=paid`.
  const parts  = ["status:open"];
  const filter = buildCustomerFilter(scope);
  if (filter) parts.unshift(filter);

  const query  = `query OpenOrder($q: String!) {
    orders(first: 1, sortKey: CREATED_AT, reverse: true, query: $q) {
      edges { node { ${ORDER_FIELDS} } }
    }
  }`;
  const data = await gql<{ orders: { edges: { node: any }[] } }>(query, { q: parts.join(" AND ") });
  let node = data.orders.edges[0]?.node;

  // Fallback: a customer may have an order that doesn't satisfy `status:open`
  // (e.g. fulfilled but not delivered, or the API returns it under a different
  // status). If we got nothing with the strict filter but the customer scope is
  // set, fall back to "any most-recent order for this customer".
  if (!node && filter) {
    const fallback = await gql<{ orders: { edges: { node: any }[] } }>(query, { q: filter });
    node = fallback.orders.edges[0]?.node;
  }

  return node ? normalizeOrder(node) : null;
}

export async function fetchProducts(limit = 4): Promise<ShopifyProductSummary[]> {
  const query = `query Products($first: Int!) { products(first: $first) { edges { node { ${PRODUCT_FIELDS} } } } }`;
  const data = await gql<{ products: { edges: { node: any }[] } }>(query, { first: limit });
  return data.products.edges.map((e) => normalizeProduct(e.node));
}

export async function fetchInventoryByLocation(): Promise<{
  product: ShopifyProductSummary | null;
  locations: ShopifyLocationSummary[];
}> {
  const query = `query InventoryByLocation {
    products(first: 1) {
      edges {
        node {
          ${PRODUCT_FIELDS}
          variants(first: 1) {
            edges {
              node {
                inventoryItem {
                  inventoryLevels(first: 4) {
                    edges {
                      node {
                        quantities(names: ["available"]) { name quantity }
                        location {
                          id
                          name
                          address { city country }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }`;
  const data = await gql<{ products: { edges: { node: any }[] } }>(query);
  const node = data.products.edges[0]?.node;
  if (!node) return { product: null, locations: [] };

  const product = normalizeProduct(node);
  const levels  = node.variants?.edges?.[0]?.node?.inventoryItem?.inventoryLevels?.edges ?? [];
  const locations: ShopifyLocationSummary[] = levels.map((e: any) => {
    const loc = e.node?.location ?? {};
    const availQty = (e.node?.quantities ?? []).find((q: any) => q.name === "available")?.quantity ?? 0;
    return {
      id:        parseGid(loc.id ?? ""),
      name:      loc.name ?? "Location",
      city:      loc.address?.city    ?? null,
      country:   loc.address?.country ?? null,
      available: availQty,
    };
  });
  return { product, locations };
}

export async function fetchCustomers(limit = 20): Promise<ShopifyCustomerSummary[]> {
  const query = `query Customers($first: Int!) {
    customers(first: $first) {
      edges {
        node {
          id
          legacyResourceId
          firstName
          lastName
          email
          phone
          numberOfOrders
          amountSpent { amount currencyCode }
          createdAt
          defaultAddress { city country phone }
          tags
        }
      }
    }
  }`;
  const data = await gql<{ customers: { edges: { node: any }[] } }>(query, { first: limit });
  return data.customers.edges.map((e) => normalizeCustomer(e.node));
}

// ─── Normalizers (map GraphQL shapes → consumer-facing summaries) ───────────

function normalizeOrder(o: any): ShopifyOrderSummary {
  const fulfillment = o.fulfillments?.[0];
  const tracking    = fulfillment?.trackingInfo?.[0];
  const money       = o.totalPriceSet?.shopMoney ?? {};
  const fStatus     = (o.displayFinancialStatus   as string | undefined)?.toLowerCase() ?? null;
  const fulfStatus  = (o.displayFulfillmentStatus as string | undefined)?.toLowerCase() ?? null;

  const numericId = parseGid(o.id) || parseInt(o.legacyResourceId ?? "0", 10) || 0;
  return {
    id:                numericId,
    name:              o.name,
    orderNumber:       parseInt((o.name ?? "").replace(/[^0-9]/g, ""), 10) || 0,
    totalPrice:        money.amount ?? "0.00",
    currency:          money.currencyCode ?? "USD",
    financialStatus:   fStatus,
    fulfillmentStatus: fulfStatus,
    createdAt:         o.createdAt,
    customerName:      [o.customer?.firstName, o.customer?.lastName].filter(Boolean).join(" ") || "Customer",
    email:             o.email ?? null,
    lineItems:         (o.lineItems?.edges ?? []).map((e: any) => ({
      title:    e.node?.title ?? "Item",
      quantity: e.node?.quantity ?? 1,
      price:    e.node?.originalUnitPriceSet?.shopMoney?.amount ?? "0.00",
      imageUrl: e.node?.image?.url ?? undefined,
    })),
    trackingNumber:  tracking?.number  ?? null,
    trackingCompany: tracking?.company ?? null,
    trackingUrl:     tracking?.url     ?? null,
    cancellable:     !o.cancelledAt && fulfStatus !== "fulfilled",
  };
}

function normalizeProduct(p: any): ShopifyProductSummary {
  const variant = p.variants?.edges?.[0]?.node ?? {};
  const numericId = parseGid(p.id) || parseInt(p.legacyResourceId ?? "0", 10) || 0;
  const qty = variant.inventoryQuantity ?? 0;
  return {
    id:                numericId,
    title:             p.title,
    vendor:            p.vendor ?? "",
    productType:       p.productType ?? "",
    price:             variant.price ?? "0.00",
    compareAtPrice:    variant.compareAtPrice ?? null,
    imageUrl:          p.featuredImage?.url ?? null,
    available:         (p.status ?? "ACTIVE").toUpperCase() === "ACTIVE" && qty > 0,
    inventoryQuantity: qty,
  };
}

function normalizeCustomer(c: any): ShopifyCustomerSummary {
  const first = c.firstName ?? "";
  const last  = c.lastName  ?? "";
  const display = [first, last].filter(Boolean).join(" ") || (c.email ?? "Customer");
  const numericId = parseInt(c.legacyResourceId ?? "0", 10) || parseGid(c.id ?? "");
  const spent = c.amountSpent ?? {};
  return {
    id:          numericId,
    firstName:   first,
    lastName:    last,
    displayName: display,
    email:       c.email ?? null,
    phone:       c.phone ?? c.defaultAddress?.phone ?? null,
    ordersCount: c.numberOfOrders != null ? Number(c.numberOfOrders) : 0,
    totalSpent:  spent.amount ?? "0.00",
    currency:    spent.currencyCode ?? "USD",
    createdAt:   c.createdAt,
    city:        c.defaultAddress?.city    ?? null,
    country:     c.defaultAddress?.country ?? null,
    tags:        Array.isArray(c.tags) ? c.tags : [],
  };
}
